/** Utils */
import { ErrorBoundary as ReactErrorBoundary, type FallbackProps } from 'react-error-boundary';
import type { ErrorInfo, FC } from 'react';
import { Outlet } from 'react-router-dom';

/** Types */
import type { ErrorBoundaryProps } from './ErrorBoundaryComponent.type';

/**
 * Put this at Page-level or Component-level,
 * if you put this at RouteCollection level, it will increase bundle size.
 */

const ErrorBoundaryComponent: FC<ErrorBoundaryProps> = props => {
  const { children } = props;

  const handleError = (error: unknown, info: ErrorInfo) => {
    console.error('Caught a boundary error: ', error, info);
  };

  return (
    <ReactErrorBoundary FallbackComponent={ErrorFallback} onError={handleError}>
      {children ?? <Outlet />}
    </ReactErrorBoundary>
  );
};

export default ErrorBoundaryComponent;

const ErrorFallback: FC<FallbackProps> = props => {
  const { error, resetErrorBoundary } = props;

  return (
    <div
      role='alert'
      className='relative m-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700'
    >
      <h2 className='mb-2 text-lg font-bold'>Something went wrong</h2>
      <pre className='mb-2 rounded bg-red-200 p-2 text-sm whitespace-pre-wrap'>
        {(error as Error).message}
      </pre>
      <button
        onClick={resetErrorBoundary}
        className='focus:shadow-outline rounded bg-red-500 px-4 py-2 font-bold text-white hover:bg-red-700 focus:outline-none'
      >
        Try again
      </button>
    </div>
  );
};
